import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Lightbulb, Pencil, Trash2, X } from 'lucide-react';
import { MappingWorkspace } from './OtaMapping';

// Normalize name+city the same way the backend group_key does (UI-only).
const normKey = (s) => (s || '').toString().trim().toLowerCase().replace(/[^\w\s]/g, '').replace(/\s+/g, ' ');

// "See All Properties" → Property Mapping tab.
// Merge duplicate properties into one final property, list existing mappings,
// and pre-fill likely-duplicate groups via the existing name+city grouping.
export const PropertyMappingTab = ({ apiBase, properties, onMerged }) => {
  const [finalName, setFinalName] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [mappings, setMappings] = useState([]);
  const [loadingMappings, setLoadingMappings] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [editing, setEditing] = useState(null);  // {id, name}
  const [otaTags, setOtaTags] = useState({});
  const [workspaceData, setWorkspaceData] = useState(null);
  const [workspaceOpen, setWorkspaceOpen] = useState(false);

  const authHdr = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${apiBase}/properties/ota-tags`, authHdr);
        setOtaTags(r?.data?.tags || {});
      } catch (_) { /* non-blocking */ }
    })();
    // eslint-disable-next-line
  }, []);

  const otaBadge = (t) => ({ MMT: 'bg-red-100 text-red-700', Agoda: 'bg-blue-100 text-blue-700', Yatra: 'bg-emerald-100 text-emerald-700' }[t] || 'bg-gray-100 text-gray-700');
  const renderMappingOtaTags = (pid) => {
    const tags = otaTags[pid] || [];
    if (!tags.length) return null;
    return (
      <span className="flex gap-1 ml-1" data-testid={`mapping-ota-tags-${pid}`}>
        {tags.map((t) => <span key={t} className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${otaBadge(t)}`} data-testid={`mapping-ota-tag-${pid}-${t}`}>{t}</span>)}
      </span>
    );
  };

  const fetchMappings = async () => {
    setLoadingMappings(true);
    try {
      const res = await axios.get(`${apiBase}/property-mappings`, authHdr);
      setMappings(res.data?.mappings || []);
    } catch (err) {
      console.error('Failed to load property mappings', err);
    } finally {
      setLoadingMappings(false);
    }
  };

  useEffect(() => { fetchMappings(); /* eslint-disable-next-line */ }, []);

  const propList = properties || [];
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return propList;
    return propList.filter(p =>
      (p.name || '').toLowerCase().includes(q) ||
      (p.grb_id || '').toLowerCase().includes(q) ||
      (p.city || '').toLowerCase().includes(q));
  }, [propList, search]);

  // Likely-duplicate groups by normalized (name, city) — reuse backend group_key.
  const suggestionGroups = useMemo(() => {
    const byKey = {};
    propList.forEach(p => {
      const k = p.group_key || normKey(`${p.name || ''} ${p.city || ''}`);
      (byKey[k] = byKey[k] || []).push(p);
    });
    return Object.values(byKey).filter(g => g.length > 1)
      .sort((a, b) => b.length - a.length);
  }, [propList]);

  const toggle = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const applySuggestion = (group) => {
    setSelectedIds(new Set(group.map(p => p.id)));
    setFinalName(group[0]?.name || '');
    setShowSuggestions(false);
    toast.info(`Pre-filled ${group.length} properties — review and Load Mapping`);
  };

  const loadWorkspace = async () => {
    if (!finalName.trim()) { toast.error('Enter a Final Property Name'); return; }
    if (selectedIds.size < 1) { toast.error('Select at least one property'); return; }
    try {
      setLoading(true);
      const ids = Array.from(selectedIds);
      const res = await axios.get(`${apiBase}/property-mapping/workspace?property_ids=${ids.join(',')}`, authHdr);
      setWorkspaceData(res.data);
      setWorkspaceOpen(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load mapping');
    } finally {
      setLoading(false);
    }
  };

  const onSaved = async () => {
    setFinalName('');
    setSelectedIds(new Set());
    setWorkspaceData(null);
    await fetchMappings();
    if (onMerged) await onMerged();
  };

  const saveEdit = async () => {
    if (!editing?.name?.trim()) { toast.error('Enter a name'); return; }
    try {
      await axios.put(`${apiBase}/property-mappings/${editing.id}`, { final_property_name: editing.name.trim() }, authHdr);
      toast.success('Mapping updated');
      setEditing(null);
      await fetchMappings();
      if (onMerged) await onMerged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update mapping');
    }
  };

  // Edit opens the existing OTA Room/Rate Mapping workspace popup, preloaded
  // with the mapping's canonical property + its saved room/rate OTA codes.
  // Saving re-runs the idempotent consolidate on the same canonical → updates
  // the existing mapping (no duplicates, no re-merge since no new victims).
  const editMapping = async (m) => {
    const cid = m.final_property_id;
    if (!cid) { toast.error('Mapping has no canonical property'); return; }
    try {
      setLoading(true);
      setFinalName(m.final_property_name || '');
      setSelectedIds(new Set([cid]));
      const res = await axios.get(`${apiBase}/property-mapping/workspace?property_ids=${cid}`, authHdr);
      setWorkspaceData(res.data);
      setWorkspaceOpen(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to open mapping for edit');
    } finally {
      setLoading(false);
    }
  };

  const deleteMapping = async (m) => {
    const reversible = !!m?.reversible;
    const confirmMsg = reversible
      ? 'Delete this mapping? The original properties and their data will be restored and become independent again.'
      : 'Delete this mapping? This mapping has no reversible snapshot. The system will attempt to reconstruct the original properties from surviving evidence; if that is not reliably possible, the mapping will be left unchanged and reported as not auto-split.';
    if (!window.confirm(confirmMsg)) return;
    try {
      const res = await axios.delete(`${apiBase}/property-mappings/${m.id}`, authHdr);
      if (res.data?.restored) {
        const names = (res.data.restored_properties || []).map(p => p.name).filter(Boolean).join(', ');
        const unresolved = (res.data.unrestored_properties || []);
        if (unresolved.length) {
          toast.warning(`Mapping deleted. Restored: ${names || 'original properties'}. Could not auto-split: ${unresolved.map(p => p.name || p.id).join(', ')}.`);
        } else {
          toast.success(`Mapping deleted — restored: ${names || 'original properties'}`);
        }
      } else if (res.data?.success === false) {
        // Not reliably restorable — the mapping was left unchanged.
        toast.error(res.data?.message || 'Original properties could not be auto-split; the mapping was left unchanged.');
        return;
      } else {
        toast.success(res.data?.message || 'Mapping record deleted');
      }
      await fetchMappings();
      if (onMerged) await onMerged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete mapping');
    }
  };

  return (
    <div className="space-y-6" data-testid="property-mapping-tab">
      {/* Create New Mapping */}
      <div className="border rounded-lg p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-800">Create New Mapping</h3>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowSuggestions(s => !s)}
            data-testid="mapping-suggestions-btn"
          >
            <Lightbulb className="w-4 h-4 mr-1" /> Suggestions ({suggestionGroups.length})
          </Button>
        </div>

        {showSuggestions && (
          <div className="border rounded-md bg-amber-50 p-3 space-y-2" data-testid="mapping-suggestions-panel">
            {suggestionGroups.length === 0 ? (
              <div className="text-xs text-gray-500">No likely duplicate groups detected.</div>
            ) : suggestionGroups.map((g, i) => (
              <div key={i} className="flex items-center justify-between gap-3 bg-white border rounded p-2" data-testid={`mapping-suggestion-${i}`}>
                <div className="text-xs text-gray-700">
                  <span className="font-semibold">{g[0]?.name}</span>
                  <span className="text-gray-500"> · {g[0]?.city || '—'} · {g.length} properties</span>
                  <div className="text-[11px] text-gray-400">{g.map(p => p.grb_id || (p.id || '').slice(0, 6)).join(', ')}</div>
                </div>
                <Button type="button" size="sm" variant="outline" onClick={() => applySuggestion(g)} data-testid={`mapping-suggestion-apply-${i}`}>
                  Use
                </Button>
              </div>
            ))}
          </div>
        )}

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Final Property Name</label>
          <Input
            value={finalName}
            onChange={(e) => setFinalName(e.target.value)}
            placeholder="e.g. Grand Residency Hotel"
            data-testid="mapping-final-name-input"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Select Properties {selectedIds.size > 0 && <span className="text-orange-600">({selectedIds.size} selected)</span>}
          </label>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search properties…"
            className="mb-2 h-8 text-sm"
            data-testid="mapping-property-search"
          />
          <div className="border rounded-md max-h-52 overflow-y-auto divide-y" data-testid="mapping-property-list">
            {filtered.length === 0 ? (
              <div className="p-3 text-xs text-gray-500">No properties found.</div>
            ) : filtered.map(p => (
              <label
                key={p.id}
                className="flex items-center gap-2 p-2 text-sm cursor-pointer hover:bg-gray-50"
                data-testid={`mapping-property-option-${p.id}`}
              >
                <Checkbox
                  checked={selectedIds.has(p.id)}
                  onCheckedChange={() => toggle(p.id)}
                  data-testid={`mapping-property-checkbox-${p.id}`}
                />
                <span className="font-medium">{p.name}</span>
                <span className="text-xs text-gray-500">· {p.grb_id || (p.id || '').slice(0, 6)} · {p.city || '—'}</span>
                {renderMappingOtaTags(p.id)}
              </label>
            ))}
          </div>
        </div>

        <div className="flex justify-end">
          <Button
            type="button"
            onClick={loadWorkspace}
            disabled={loading || selectedIds.size < 1 || !finalName.trim()}
            className="bg-gradient-to-r from-orange-500 to-amber-600 text-white"
            data-testid="mapping-load-btn"
          >
            {loading ? 'Loading…' : 'Load Mapping'}
          </Button>
        </div>
      </div>

      <MappingWorkspace
        apiBase={apiBase}
        finalName={finalName}
        propertyIds={Array.from(selectedIds)}
        data={workspaceData}
        open={workspaceOpen}
        onOpenChange={setWorkspaceOpen}
        onSaved={onSaved}
      />

      {/* Existing Mappings */}
      <div className="border rounded-lg p-4 space-y-3">
        <h3 className="text-sm font-semibold text-gray-800">Existing Mappings</h3>
        {loadingMappings ? (
          <div className="text-xs text-gray-500">Loading…</div>
        ) : mappings.length === 0 ? (
          <div className="text-xs text-gray-500" data-testid="mapping-existing-empty">No mappings yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="mapping-existing-table">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">Final Property Name</th>
                  <th className="px-3 py-2 text-left">Mapped Properties</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {mappings.map(m => (
                  <tr key={m.id} className="border-t" data-testid={`mapping-row-${m.id}`}>
                    <td className="px-3 py-2 font-medium">
                      {editing?.id === m.id ? (
                        <div className="flex items-center gap-1">
                          <Input
                            value={editing.name}
                            onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                            className="h-7 text-sm"
                            data-testid={`mapping-edit-input-${m.id}`}
                          />
                          <Button type="button" size="sm" onClick={saveEdit} className="h-7 px-2 bg-emerald-600 text-white" data-testid={`mapping-edit-save-${m.id}`}>Save</Button>
                          <Button type="button" size="sm" variant="outline" className="h-7 px-2" onClick={() => setEditing(null)} data-testid={`mapping-edit-cancel-${m.id}`}><X className="w-3 h-3" /></Button>
                        </div>
                      ) : m.final_property_name}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-600">
                      {(m.mapped_properties || []).map(mp => mp.name).join(', ')}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <Button type="button" size="sm" variant="outline" className="h-7 px-2 mr-1" onClick={() => editMapping(m)} data-testid={`mapping-edit-btn-${m.id}`}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-red-600 border-red-200 hover:bg-red-50" onClick={() => deleteMapping(m)} data-testid={`mapping-delete-btn-${m.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertyMappingTab;
